import { Component } from '@theme/component';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollTo,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';
import { clamp, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {object} BlogPostArticleRefs
 * @property {HTMLElement} content - The article body wrapper.
 * @property {HTMLElement} [toc] - The table of contents aside.
 * @property {HTMLDetailsElement} [tocDetails] - The disclosure wrapping the list.
 * @property {HTMLOListElement} [tocList] - The list the entries are rendered into.
 * @property {HTMLElement} [progress] - The fixed reading progress track.
 */

/**
 * Article reading aids: a table of contents built from the post's H2s with
 * scroll-spy, and a reading progress bar.
 *
 * Both are scroll-driven, and this theme does not scroll the window on desktop —
 * at >=990px `html`/`body` are locked to 100dvh and `.page-wrapper` scrolls instead
 * (assets/base.css). Everything scroll-related therefore goes through
 * `@theme/scroll-container` rather than `window.scrollY`, and both the scroll
 * listener and the IntersectionObserver root are rebound when the viewport crosses
 * that breakpoint. `assets/header.js` is the reference for this pattern.
 *
 * @extends Component<BlogPostArticleRefs>
 */
class BlogPostArticle extends Component {
  requiredRefs = ['content'];

  /** @type {HTMLElement[]} */
  #headings = [];
  /** @type {HTMLAnchorElement[]} */
  #links = [];
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | null} */
  #rafId = null;
  /** @type {number} */
  #activeIndex = -1;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#buildToc();
    this.#bindScroll();
    this.#observeHeadings();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });

    // The progress ratio depends on the article's height, which changes as
    // images and embeds load.
    this.#resizeObserver = new ResizeObserver(this.#requestUpdate);
    this.#resizeObserver.observe(this.refs.content);

    this.#syncDisclosure();
    this.#requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownScroll();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /**
   * The Section Rendering API can replace this subtree; rebuild against the new DOM.
   */
  updatedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#buildToc();
    this.#observeHeadings();
    this.#syncDisclosure();
    this.#requestUpdate();
  }

  // -- table of contents ----------------------------------------------------

  /**
   * Collects H2s from the rendered article and renders the list.
   *
   * The body is a single opaque rich-text blob from Shopify's editor, so the
   * headings only exist once rendered — there is no reliable way to extract them
   * in Liquid.
   */
  #buildToc() {
    const { content, toc, tocList } = this.refs;
    if (!toc || !tocList) return;

    this.#headings = /** @type {HTMLElement[]} */ ([...content.querySelectorAll('h2')]).filter(
      (heading) => (heading.textContent ?? '').trim().length > 0
    );

    const minimum = Number(this.dataset.tocMinHeadings ?? 2);

    // A one-entry contents list is noise, and an empty one leaves a dead column.
    if (this.#headings.length < minimum) {
      this.#headings = [];
      this.#links = [];
      tocList.replaceChildren();
      toc.hidden = true;
      this.classList.remove('blog-article--has-toc');
      return;
    }

    /** @type {Set<string>} */
    const used = new Set();
    const fragment = document.createDocumentFragment();
    this.#links = [];

    this.#headings.forEach((heading, index) => {
      if (!heading.id) heading.id = this.#uniqueSlug(heading.textContent ?? '', index, used);
      used.add(heading.id);
      heading.classList.add('blog-article__heading');

      const item = document.createElement('li');
      item.className = 'blog-article__toc-item';

      const link = document.createElement('a');
      link.className = 'blog-article__toc-link';
      link.href = `#${heading.id}`;
      link.textContent = (heading.textContent ?? '').trim();
      link.addEventListener('click', (event) => this.#handleLinkClick(event, index));

      item.append(link);
      fragment.append(item);
      this.#links.push(link);
    });

    tocList.replaceChildren(fragment);
    toc.hidden = false;
    this.classList.add('blog-article--has-toc');
  }

  /**
   * @param {string} text
   * @param {number} index
   * @param {Set<string>} used
   * @returns {string}
   */
  #uniqueSlug(text, index, used) {
    const base =
      text
        .trim()
        .toLowerCase()
        .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || `section-${index + 1}`;

    let candidate = base;
    let suffix = 2;
    while (used.has(candidate) || (document.getElementById(candidate) && document.getElementById(candidate) !== null)) {
      candidate = `${base}-${suffix}`;
      suffix += 1;
    }
    return candidate;
  }

  /**
   * @param {MouseEvent} event
   * @param {number} index
   */
  #handleLinkClick(event, index) {
    const heading = this.#headings[index];
    if (!heading) return;

    event.preventDefault();

    const container = getScrollContainer();
    const offset = this.#offsetWithin(heading, container) - this.#stickyOffset();

    scrollTo({
      top: Math.max(0, offset),
      // .page-wrapper carries CSS `scroll-behavior: smooth`, so 'instant' has to be
      // explicit to honour a reduced-motion preference.
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });

    // Keep the URL shareable without letting the browser perform its own jump.
    history.replaceState(history.state, '', `#${heading.id}`);
    this.#setActive(index);
  }

  /**
   * Distance from the top of the scroll container's content to an element.
   *
   * The two scroll containers need different maths. `.page-wrapper` is a normal
   * overflow container: its own box stays put while its contents scroll, so the
   * element's offset is measured against it. The document's scrolling element
   * instead moves with the scroll, and its `getBoundingClientRect().top` is
   * already `-scrollTop` — subtracting it there would add the scroll position a
   * second time and the result would drift at double speed.
   *
   * @param {HTMLElement} element
   * @param {Element} container
   * @returns {number}
   */
  #offsetWithin(element, container) {
    const top = element.getBoundingClientRect().top;

    if (container === document.scrollingElement || container === document.documentElement) {
      return top + container.scrollTop;
    }

    return top - container.getBoundingClientRect().top + container.scrollTop;
  }

  /** @returns {number} Height of the sticky header, if any. */
  #stickyOffset() {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed + 16 : 16;
  }

  // -- scroll spy -----------------------------------------------------------

  #observeHeadings() {
    if (!this.#headings.length) return;

    // Activate a heading once it reaches the upper third of the viewport, so the
    // highlighted entry matches what the reader is actually looking at.
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: getIntersectionRoot(),
      rootMargin: `-${Math.round(this.#stickyOffset())}px 0px -66% 0px`,
      threshold: 0,
    });

    for (const heading of this.#headings) this.#observer.observe(heading);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    const visible = entries.filter((entry) => entry.isIntersecting);

    if (visible.length > 0) {
      const first = visible.reduce((a, b) => (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
      this.#setActive(this.#headings.indexOf(/** @type {HTMLElement} */ (first.target)));
      return;
    }

    // Nothing intersecting means we are mid-section, below its heading — fall back
    // to the last heading scrolled past.
    this.#setActiveFromScroll();
  };

  #setActiveFromScroll() {
    if (!this.#headings.length) return;

    const container = getScrollContainer();
    const threshold = getScrollTop() + this.#stickyOffset() + 1;

    let index = -1;
    for (let i = 0; i < this.#headings.length; i += 1) {
      const heading = this.#headings[i];
      if (!heading) continue;
      if (this.#offsetWithin(heading, container) <= threshold) index = i;
      else break;
    }
    this.#setActive(index);
  }

  /** @param {number} index */
  #setActive(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    this.#links.forEach((link, i) => {
      const active = i === index;
      link.classList.toggle('blog-article__toc-link--active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
  }

  // -- progress -------------------------------------------------------------

  #bindScroll() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#requestUpdate, { passive: true });
  }

  #teardownScroll() {
    this.#scrollTarget?.removeEventListener('scroll', this.#requestUpdate);
    this.#scrollTarget = null;
  }

  #requestUpdate = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateProgress();
      if (!this.#headings.length) return;
      // IntersectionObserver alone cannot distinguish "above the first heading"
      // from "below the last", so keep the fallback in sync while scrolling.
      this.#setActiveFromScroll();
    });
  };

  /**
   * Progress across the article body only.
   *
   * Measuring the whole page would count the header and footer as unread content,
   * so the bar would never reach either end.
   */
  #updateProgress() {
    const { content, progress } = this.refs;
    if (!progress) return;

    const container = getScrollContainer();
    const start = this.#offsetWithin(content, container) - this.#stickyOffset();
    const distance = content.offsetHeight - container.clientHeight + this.#stickyOffset();

    // Article shorter than the viewport: nothing to track.
    if (!Number.isFinite(distance) || distance <= 0) {
      progress.style.setProperty('--reading-progress', '0');
      progress.hidden = true;
      return;
    }

    progress.hidden = false;
    const ratio = clamp((getScrollTop() - start) / distance, 0, 1);
    progress.style.setProperty('--reading-progress', `${ratio}`);
  }

  // -- breakpoint -----------------------------------------------------------

  /**
   * The scroll container swaps between `.page-wrapper` and the document at 990px,
   * so cached bindings go stale on resize.
   */
  #handleBreakpointChange = () => {
    this.#teardownScroll();
    this.#bindScroll();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#observeHeadings();

    this.#syncDisclosure();
    this.#requestUpdate();
  };

  /**
   * The contents list is a disclosure on mobile and always-open on desktop.
   */
  #syncDisclosure() {
    const { tocDetails } = this.refs;
    if (!tocDetails) return;
    if (scrollContainerMediaQuery.matches) tocDetails.open = true;
  }
}

if (!customElements.get('blog-post-article-component')) {
  customElements.define('blog-post-article-component', BlogPostArticle);
}
